/**
 * School color customization — curated color palette and utilities
 * to generate iPad-style wallpaper gradients from a chosen school color.
 */

/** Curated color options for the school color picker */
export const THEME_COLORS = [
  { hex: '#ef4444', label: 'Red' },
  { hex: '#f97316', label: 'Orange' },
  { hex: '#f59e0b', label: 'Amber' },
  { hex: '#eab308', label: 'Gold' },
  { hex: '#84cc16', label: 'Lime' },
  { hex: '#22c55e', label: 'Green' },
  { hex: '#10b981', label: 'Emerald' },
  { hex: '#14b8a6', label: 'Teal' },
  { hex: '#06b6d4', label: 'Cyan' },
  { hex: '#0ea5e9', label: 'Sky' },
  { hex: '#3b82f6', label: 'Blue' },
  { hex: '#6366f1', label: 'Indigo' },
  { hex: '#8b5cf6', label: 'Violet' },
  { hex: '#a855f7', label: 'Purple' },
  { hex: '#d946ef', label: 'Fuchsia' },
  { hex: '#ec4899', label: 'Pink' },
  { hex: '#f43f5e', label: 'Rose' },
  { hex: '#78716c', label: 'Stone' },
]

/** A single color stop in a school brand gradient */
export interface GradientStop {
  /** Hex color, e.g. '#3b82f6' */
  color: string
  /** Position along the gradient, 0-100 */
  position: number
}

/** Structured school brand gradient — the app generates the CSS from this (never stores raw CSS) */
export interface SchoolGradient {
  type: 'linear' | 'radial'
  /** Angle in degrees for linear gradients (ignored for radial) */
  angle: number
  /** 2-4 color stops */
  stops: GradientStop[]
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/

/** Type guard + validator for a persisted/incoming gradient value (used by API + UI) */
export function isValidGradient(v: unknown): v is SchoolGradient {
  if (!v || typeof v !== 'object') return false
  const g = v as Record<string, unknown>
  if (g.type !== 'linear' && g.type !== 'radial') return false
  if (typeof g.angle !== 'number' || g.angle < 0 || g.angle > 360) return false
  if (!Array.isArray(g.stops) || g.stops.length < 2 || g.stops.length > 4) return false
  return g.stops.every(s => {
    if (!s || typeof s !== 'object') return false
    const stop = s as Record<string, unknown>
    return typeof stop.color === 'string' && HEX_RE.test(stop.color)
      && typeof stop.position === 'number' && stop.position >= 0 && stop.position <= 100
  })
}

/** Build the CSS `background` value for a school gradient (vivid — for the login hero) */
export function gradientToCss(g: SchoolGradient): string {
  const stops = g.stops.map(s => `${s.color} ${Math.round(s.position)}%`).join(', ')
  return g.type === 'radial'
    ? `radial-gradient(circle at 50% 30%, ${stops})`
    : `linear-gradient(${Math.round(g.angle)}deg, ${stops})`
}

/**
 * Softened version of a school gradient for in-app wallpaper — keeps the hue
 * transition but lifts every stop to a gentle pastel so content stays readable.
 * Matches the tone of generateThemeStyles() so hubs + login feel like one brand.
 */
export function softenGradient(g: SchoolGradient): string {
  const stops = g.stops.map(s => {
    const { h, s: sat } = hexToHSL(s.color)
    const bgSat = Math.min(sat, 70)
    return `hsl(${h}, ${bgSat}%, 93%) ${Math.round(s.position)}%`
  }).join(', ')
  return g.type === 'radial'
    ? `radial-gradient(circle at 50% 30%, ${stops})`
    : `linear-gradient(${Math.round(g.angle)}deg, ${stops})`
}

/**
 * Background style for any school-tinted surface (settings card, People & Access,
 * etc.): softened brand gradient when set, else the legacy single color, else the
 * app default (undefined). One source of truth so these surfaces never drift.
 */
export function schoolBackgroundStyle(
  school: { theme_color: string | null; theme_gradient: SchoolGradient | null },
): React.CSSProperties | undefined {
  if (school.theme_gradient) return { background: softenGradient(school.theme_gradient) }
  if (school.theme_color) return generateThemeStyles(school.theme_color).bgStyle
  return undefined
}

const clampNum = (n: number, lo: number, hi: number) => Math.min(Math.max(n, lo), hi)

/** Linear-interpolate between two #rrggbb hex colors (t in 0..1) */
export function lerpHex(a: string, b: string, t: number): string {
  const chans = (hex: string) => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16))
  const ca = chans(a), cb = chans(b)
  const mix = ca.map((v, i) => Math.round(v + (cb[i] - v) * clampNum(t, 0, 1)))
  return '#' + mix.map(v => v.toString(16).padStart(2, '0')).join('')
}

/** Color the gradient shows at a given position (for sensible new-stop defaults) */
export function interpolateColorAt(g: SchoolGradient, position: number): string {
  const stops = [...g.stops].sort((a, b) => a.position - b.position)
  if (position <= stops[0].position) return stops[0].color
  const last = stops[stops.length - 1]
  if (position >= last.position) return last.color
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i], b = stops[i + 1]
    if (position >= a.position && position <= b.position) {
      const span = b.position - a.position || 1
      return lerpHex(a.color, b.color, (position - a.position) / span)
    }
  }
  return last.color
}

/** Insert a stop at `position` with an interpolated color, keeping stops sorted (max 4) */
export function addGradientStop(g: SchoolGradient, position: number): SchoolGradient {
  if (g.stops.length >= 4) return g
  const pos = Math.round(clampNum(position, 0, 100))
  const stops = [...g.stops, { color: interpolateColorAt(g, pos), position: pos }]
    .sort((a, b) => a.position - b.position)
  return { ...g, stops }
}

/** Remove the stop at `index`, never going below 2 stops */
export function removeGradientStop(g: SchoolGradient, index: number): SchoolGradient {
  if (g.stops.length <= 2) return g
  return { ...g, stops: g.stops.filter((_, i) => i !== index) }
}

/** Recolor the stop at `index` */
export function setGradientStopColor(g: SchoolGradient, index: number, color: string): SchoolGradient {
  return { ...g, stops: g.stops.map((s, i) => (i === index ? { ...s, color } : s)) }
}

/** Move the stop at `index` to a new position, clamped between its neighbors (keeps order stable) */
export function moveGradientStop(g: SchoolGradient, index: number, position: number): SchoolGradient {
  const lo = index > 0 ? g.stops[index - 1].position + 1 : 0
  const hi = index < g.stops.length - 1 ? g.stops[index + 1].position - 1 : 100
  const pos = Math.round(clampNum(position, Math.min(lo, hi), Math.max(lo, hi)))
  return { ...g, stops: g.stops.map((s, i) => (i === index ? { ...s, position: pos } : s)) }
}

/** Curated one-tap gradient presets for schools who don't want to fiddle with stops */
export const GRADIENT_PRESETS: { label: string; gradient: SchoolGradient }[] = [
  { label: 'Sunset', gradient: { type: 'linear', angle: 135, stops: [{ color: '#f97316', position: 0 }, { color: '#ec4899', position: 100 }] } },
  { label: 'Ocean', gradient: { type: 'linear', angle: 135, stops: [{ color: '#0ea5e9', position: 0 }, { color: '#6366f1', position: 100 }] } },
  { label: 'Meadow', gradient: { type: 'linear', angle: 135, stops: [{ color: '#84cc16', position: 0 }, { color: '#14b8a6', position: 100 }] } },
  { label: 'Berry', gradient: { type: 'linear', angle: 135, stops: [{ color: '#8b5cf6', position: 0 }, { color: '#d946ef', position: 100 }] } },
  { label: 'Citrus', gradient: { type: 'linear', angle: 135, stops: [{ color: '#eab308', position: 0 }, { color: '#f97316', position: 100 }] } },
  { label: 'Cotton Candy', gradient: { type: 'linear', angle: 135, stops: [{ color: '#ec4899', position: 0 }, { color: '#8b5cf6', position: 100 }] } },
  { label: 'Glow', gradient: { type: 'radial', angle: 0, stops: [{ color: '#f59e0b', position: 0 }, { color: '#ec4899', position: 70 }, { color: '#6366f1', position: 100 }] } },
  { label: 'Aurora', gradient: { type: 'radial', angle: 0, stops: [{ color: '#14b8a6', position: 0 }, { color: '#6366f1', position: 60 }, { color: '#a855f7', position: 100 }] } },
]

/** Convert hex color to HSL values */
export function hexToHSL(hex: string): { h: number; s: number; l: number } {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2

  if (max === min) return { h: 0, s: 0, l: Math.round(l * 100) }

  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

  let h = 0
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
  else if (max === g) h = ((b - r) / d + 2) / 6
  else h = ((r - g) / d + 4) / 6

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  }
}

/**
 * Generate background gradient + blob styles from a theme color hex.
 * Creates soft pastel tints that work as an app wallpaper.
 */
export function generateThemeStyles(hex: string) {
  const { h, s } = hexToHSL(hex)
  // Use a softer saturation for the background to keep it gentle
  const bgSat = Math.min(s, 70)

  return {
    bgStyle: {
      background: `linear-gradient(to bottom right, hsl(${h}, ${bgSat}%, 95%), hsl(${h}, ${Math.max(0, bgSat - 10)}%, 93%), hsl(${h}, ${bgSat}%, 89%))`,
    } as React.CSSProperties,
    blob1Style: {
      backgroundColor: `hsla(${h}, ${bgSat}%, 80%, 0.25)`,
    } as React.CSSProperties,
    blob2Style: {
      backgroundColor: `hsla(${(h + 30) % 360}, ${Math.max(0, bgSat - 15)}%, 78%, 0.25)`,
    } as React.CSSProperties,
  }
}
